import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from 'core/api/queryKeys';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi, buildPatternFormData } from 'features/assessment-dashboard/assessment.api';
import { API_BASE_URL } from 'core/api/endpoints';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { Button } from 'shared/components/Button';
import { ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { ValidationSummary, focusFirstInvalid } from 'shared/components/ValidationSummary';
import { useToast } from 'shared/components/Toast';
import { BilingualField, ColorField, FileField, SwitchField, TextField } from 'shared/forms/Fields';
import { normalizeShortCode, resolveStorageUrl, safeHttpUrl } from 'shared/utilities/format';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

const schema = z.object({
  slug: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
  short_code: z.string().max(4).regex(/^$|^[A-Za-z]{1,4}$/).optional(),
  name_ar: z.string().min(1),
  name_en: z.string().min(1),
  description_ar: z.string().optional(),
  description_en: z.string().optional(),
  color: z.string().regex(/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/),
  is_active: z.boolean(),
});

const emptyForm = {
  slug: '',
  short_code: '',
  name_ar: '',
  name_en: '',
  description_ar: '',
  description_en: '',
  color: '#0F766E',
  is_active: true,
};

export function PatternFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState('');
  const [existingIconUrl, setExistingIconUrl] = useState('');
  const [backfill, setBackfill] = useState('');
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [zeroNotice, setZeroNotice] = useState(null);
  useUnsavedChanges(dirty);

  const detailQuery = useQuery({
    queryKey: queryKeys.pattern(id),
    enabled: isEdit,
    queryFn: ({ signal }) => assessmentApi.showPattern(id, { signal }),
  });

  useEffect(() => {
    const pattern = detailQuery.data?.pattern;
    if (!pattern) {
      return;
    }
    setForm({
      slug: pattern.slug || '',
      short_code: pattern.short_code || '',
      name_ar: pattern.name_ar || '',
      name_en: pattern.name_en || '',
      description_ar: pattern.description_ar || '',
      description_en: pattern.description_en || '',
      color: pattern.color || '#0F766E',
      is_active: Boolean(pattern.is_active),
    });
    setExistingIconUrl(
      safeHttpUrl(pattern.icon_url)
      || resolveStorageUrl(pattern.icon, API_BASE_URL)
      || '',
    );
    setIconFile(null);
    setIconPreviewUrl('');
  }, [detailQuery.data]);

  const changeIconFile = (file) => {
    setDirty(true);
    setIconFile(file);
    setIconPreviewUrl((current) => {
      if (current.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return file ? URL.createObjectURL(file) : '';
    });
  };

  useEffect(() => () => {
    if (iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
  }, [iconPreviewUrl]);

  const mutation = useMutation({
    mutationFn: (payload) => (isEdit ? assessmentApi.updatePattern(id, payload) : assessmentApi.createPattern(payload)),
    onSuccess: async (data) => {
      flushSync(() => setDirty(false));
      await queryClient.invalidateQueries({ queryKey: queryKeys.patterns });
      toast.success(isEdit ? t('status.updated') : t('status.created'));
      if (!isEdit && data.weight_configuration_required) {
        navigate(paths.weightMatrix(data.pattern.id), { replace: true, state: { progress: data } });
        return;
      }
      if (!isEdit && data.initialized_with_zero_weights) {
        setZeroNotice(data.pattern);
        return;
      }
      navigate(paths.patterns);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) {
        setErrors(mapFieldErrors(error.errors));
      }
      if (error instanceof ApiError && error.isConflict()) {
        setConflict(error.message || t('patterns.shortCodeImmutable'));
        toast.error(t('patterns.shortCodeImmutable'));
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const change = (key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setErrors({});
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const next = {};
      parsed.error.issues.forEach((issue) => {
        next[issue.path.join('.')] = [issue.message];
      });
      setErrors(next);
      focusFirstInvalid(next, { slug: 'slug', short_code: 'short_code', name_ar: 'name-ar', name_en: 'name-en' });
      return;
    }
    if (!isEdit && !backfill) {
      setErrors({ backfill_mode: [t('patterns.backfill')] });
      return;
    }
    mutation.mutate(isEdit
      ? (iconFile
        ? buildPatternFormData({ ...parsed.data, short_code: normalizeShortCode(parsed.data.short_code) || null }, iconFile)
        : { ...parsed.data, short_code: normalizeShortCode(parsed.data.short_code) || null })
      : (iconFile
        ? buildPatternFormData({
          ...parsed.data,
          short_code: normalizeShortCode(parsed.data.short_code) || null,
          backfill_mode: backfill,
        }, iconFile)
        : { ...parsed.data, short_code: normalizeShortCode(parsed.data.short_code) || null, backfill_mode: backfill }));
  };

  if (isEdit && detailQuery.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (isEdit && detailQuery.isError) {
    return <ErrorState title={humanizeApiError(detailQuery.error, t)} onRetry={detailQuery.refetch} retryLabel={t('actions.retry')} />;
  }

  if (zeroNotice) {
    return (
      <div>
        <PageHeader title={t('patterns.create')} />
        <p className="warning-banner" role="status">{t('patterns.zeroConfirm')}</p>
        <p className="warning-banner">{t('patterns.zeroWarning')}</p>
        <Link className="btn btn-primary" to={paths.weightMatrix(zeroNotice.id)}>{t('actions.configureWeights')}</Link>
      </div>
    );
  }

  return (
    <form className="stack-form" onSubmit={onSubmit} noValidate>
      <PageHeader title={isEdit ? t('actions.edit') : t('patterns.create')} backTo={paths.patterns} />
      {conflict ? <p className="warning-banner" role="alert">{conflict} {t('conflict.keepLocal')}</p> : null}
      <ValidationSummary errors={errors} title={t('forms.validationSummary')} fieldIds={{ slug: 'slug', short_code: 'short_code', name_ar: 'name-ar', name_en: 'name-en', backfill_mode: 'slug' }} />
      <TextField id="slug" label={t('patterns.slug')} value={form.slug} onChange={(value) => change('slug', value)} error={errors.slug?.[0]} required />
      <TextField
        id="short_code"
        label={t('patterns.shortCode')}
        value={form.short_code}
        onChange={(value) => change('short_code', normalizeShortCode(value))}
        error={errors.short_code?.[0]}
        hint={t('patterns.shortCodeHint')}
      />
      <BilingualField
        id="name"
        labelAr={t('patterns.nameAr')}
        labelEn={t('patterns.nameEn')}
        valueAr={form.name_ar}
        valueEn={form.name_en}
        onChangeAr={(value) => change('name_ar', value)}
        onChangeEn={(value) => change('name_en', value)}
        errorAr={errors.name_ar?.[0]}
        errorEn={errors.name_en?.[0]}
      />
      <BilingualField
        id="description"
        labelAr={t('patterns.descriptionAr')}
        labelEn={t('patterns.descriptionEn')}
        valueAr={form.description_ar}
        valueEn={form.description_en}
        onChangeAr={(value) => change('description_ar', value)}
        onChangeEn={(value) => change('description_en', value)}
        errorAr={errors.description_ar?.[0]}
        errorEn={errors.description_en?.[0]}
        multiline
        required={false}
      />
      <ColorField id="color" label={t('patterns.color')} value={form.color} onChange={(value) => change('color', value)} error={errors.color?.[0]} />
      <FileField
        id="icon"
        label={t('patterns.icon')}
        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
        file={iconFile}
        onChange={changeIconFile}
        error={errors.icon?.[0]}
        hint={t('patterns.iconHint')}
        previewUrl={iconPreviewUrl || existingIconUrl}
      />
      <SwitchField id="active" label={t('patterns.active')} checked={form.is_active} onChange={(value) => change('is_active', value)} />
      {!isEdit ? (
        <fieldset className="choice-set">
          <legend>{t('patterns.backfill')}</legend>
          <label>
            <input type="radio" name="backfill" checked={backfill === 'configure_now'} onChange={() => { setBackfill('configure_now'); setDirty(true); }} />
            {t('patterns.configureNow')}
          </label>
          <label>
            <input type="radio" name="backfill" checked={backfill === 'zero_now'} onChange={() => { setBackfill('zero_now'); setDirty(true); }} />
            {t('patterns.zeroNow')}
          </label>
          {errors.backfill_mode?.[0] ? <span className="field-error">{errors.backfill_mode[0]}</span> : null}
        </fieldset>
      ) : null}
      <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>{t('actions.save')}</Button>
    </form>
  );
}
